import { Button } from '@creditornot/cb-button';
import { DatePickerInput } from '@creditornot/cb-date-picker';
import { cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { DateTime } from 'luxon';
import React, { useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { exportMenu } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { WoltModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';
import { download } from 'common/utils';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import { reportMenuExportWithDatetime } from 'modules/menu/behavioral';
import { trackExportPastMenu } from 'modules/menu/hooks/telemetryHooks';

type Props = {
  className?: string;
  onClose: () => void;
  venueSlug: string;
};

const Root = styled(WoltModal)`
  position: relative;
  background: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
  border-radius: 8px;
  width: 400px;
  display: flex;
  overflow-y: visible;
  flex-direction: column;
  padding: 0;

  ${elevationCss(1)}
`;

const StyledContent = styled(ModalContent)`
  display: flex;
  flex-direction: column;
`;

const DateTimeContainer = styled.div`
  display: flex;
`;

const InfoMessage = styled(InputAlert).attrs({ size: 'small', variant: 'info' })`
  margin-block-start: 16px;
`;

const StyledFooter = styled(ModalFooter)`
  justify-content: flex-end;
`;

const StyledTimePicker = styled(TimePicker)`
  width: 120px;
  margin-inline-start: 16px;
`;

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'secondary' })`
  margin-inline-end: 16px;
`;

const ConfirmButton = styled(Button).attrs({ size: 'small', variant: 'primary' })``;

const buildDateTimeIsoString = (dateValue: Date, timeValue: number) => {
  const date = DateTime.fromObject(
    {
      year: dateValue.getFullYear(),
      month: dateValue.getMonth() + 1,
      day: dateValue.getDate(),
    },
    {
      zone: 'utc',
    },
  ).startOf('day');

  const combinedDate = date.plus({ minutes: timeValue });

  return combinedDate.toISO();
};

const ExportMenuWithDatetimeModal = ({ className, onClose, venueSlug }: Props) => {
  const { menuId } = useContext(MenuMetaContext);
  const intl = useIntl();

  const [dateValue, setDateValue] = useState<Date | undefined>(undefined);
  const [timeValue, setTimeValue] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (dateValue === undefined) {
      return;
    }
    reportMenuExportWithDatetime();
    setLoading(true);
    try {
      const isoDateString = buildDateTimeIsoString(dateValue, timeValue);
      const menuData = await exportMenu(menuId, { when: isoDateString });
      const json = JSON.stringify(menuData);
      download([json], 'application/json', `${venueSlug}-${isoDateString}.json`);
      toast.success(intl.formatMessage({ id: 'menu.exportMenuSuccess' }));
      trackExportPastMenu('Success', dateValue);
    } catch (error) {
      trackExportPastMenu(`Failure: ${error}`, dateValue);
      toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
    }
    setLoading(false);
    onClose();
  };

  return (
    <Root className={className} isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="menu.export" />
      </ModalHeader>
      <StyledContent>
        <DateTimeContainer>
          <DatePickerInput
            placeholder={intl.formatMessage({ id: 'date-time.date-picker-placeholder' })}
            popoverZIndex={100002}
            value={dateValue}
            onChange={val => setDateValue(val)}
          />
          <StyledTimePicker value={timeValue} onChange={setTimeValue} />
        </DateTimeContainer>
        <InfoMessage>
          <FormattedMessage id="menu.exportPastMenuInfo" />
        </InfoMessage>
      </StyledContent>
      <StyledFooter>
        <CancelButton onClick={onClose} disabled={loading}>
          <FormattedMessage id="state.cancel" />
        </CancelButton>
        <ConfirmButton onClick={handleSubmit} disabled={dateValue === undefined || loading} loading={loading}>
          <FormattedMessage id="menu.export" />
        </ConfirmButton>
      </StyledFooter>
    </Root>
  );
};

export default ExportMenuWithDatetimeModal;
